import {App, Component, Plugin} from 'vue';

import JoyPrimeVue from './components/JoyPrimeVue.vue';
import JoyMultiSelect from './components/JoyMultiSelect.vue';
import JoyAutoComplete from './components/JoyAutoComplete.vue';
import JoyDataTable from './components/JoyDataTable.vue';
import VJoyCalendar from './components/VJoyCalendar/VJoyCalendar.vue';

/**
 * Cherry-picked components from PrimeVue
 */
import PrimeVue from 'primevue/config';

import MultiSelect from 'primevue/multiselect';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import AutoComplete from 'primevue/autocomplete';

const COMPONENTS_NAMES = ['multiselect', 'datatable', 'autocomplete', 'calendar'] as const;

type TComponent = {
    name: string;
    component: Component;
    /**
     * Abtract component name to register. See joyComponent for details
     */
    joyName?: string;
    /**
     * This is the abstract component that automatically includes the CSS override
     */
    joyComponent?: Component;
};

const COMPONENTS_TO_REGISTER: {
    [key in (typeof COMPONENTS_NAMES)[number]]?: TComponent | TComponent[];
} = {
    multiselect: {
        name: 'MultiSelect',
        component: MultiSelect,
        joyName: 'JoyMultiSelect',
        joyComponent: JoyMultiSelect,
    },
    autocomplete: {
        name: 'AutoComplete',
        component: AutoComplete,
        joyName: 'JoyAutoComplete',
        joyComponent: JoyAutoComplete,
    },
    calendar: {
        name: 'VJoyCalendar',
        component: VJoyCalendar,
    },
    datatable: [
        {
            name: 'DataTable',
            component: DataTable,
            joyName: 'JoyDataTable',
            joyComponent: JoyDataTable,
        },
        {
            name: 'Column',
            component: Column,
        },
        {
            name: 'ColumnGroup',
            component: ColumnGroup,
        },
        {
            name: 'Row',
            component: Row,
        },
    ],
};

const RegisterExternalComponentsPlugin: Plugin<{registerExternalComponents: (typeof COMPONENTS_NAMES)[number][]}> = {
    install: (app: App, options: {registerExternalComponents: (typeof COMPONENTS_NAMES)[number][]}) => {
        if (!options.registerExternalComponents || !options.registerExternalComponents.length) {
            return;
        }

        app.use(PrimeVue, {
            zIndex: {
                overlay: 1053,
            },
        });

        // Contains all default primevue styles
        app.component('JoyPrimeVue', JoyPrimeVue);

        options.registerExternalComponents.map((cmpAlias) => {
            const componentsToRegister = COMPONENTS_TO_REGISTER[cmpAlias];

            if (!componentsToRegister) {
                return;
            }

            if (Array.isArray(componentsToRegister)) {
                componentsToRegister.forEach(registerComponent);
            } else {
                registerComponent(componentsToRegister);
            }
        });

        function registerComponent(component: TComponent) {
            if (component['joyName'] && component['joyComponent']) {
                app.component(component['joyName'], component['joyComponent']);
            }
            app.component(component['name'], component['component']);
        }
    },
};

export {RegisterExternalComponentsPlugin, COMPONENTS_NAMES};
